import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import DashboardLayout from "../../../layouts/DashboardLayout";
import CeroButton from "../../../components/CeroButton";
import useStyles from "./styles";
import CeroPopUp from "../../../components/CeroPopUp";
import SendQuestions from "./SendQuestions";
import CeroInput from "../../../components/CeroInput";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  getCountryList,
  getStates,
  getSustainabilityRiskGenericQuestions,
} from "../../../redux/actions";
import { getCookie } from "../../../services/cookie";

const SustainabilityRiskAssessmentSurvey = () => {
  const classes = useStyles();
  const readOnlyMode = getCookie("read_only_mode") === "disable_edit";
  const [open, setOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState({});
  const dispatch = useDispatch();
  const [country, setcountry] = useState("");

  const sendQuestionnairesStatus = useSelector(
    (state) => state.sustainabilityRiskAssessment.getGenericQuestions.status
  );

  const generalQuestionsList = useSelector((state) => {
    const data = state.sustainabilityRiskAssessment?.getGenericQuestions?.data;
    return Array.isArray(data) ? data : [];
  });
  useEffect(() => {
    dispatch(getSustainabilityRiskGenericQuestions());
    dispatch(getCountryList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getStates(country.id));
  }, [country]);

  const handleCheckboxChange = (event, questionId) => {
    const value = event.target.value;
    setSelectedValues((prevValues) => {
      const questionValues = prevValues[questionId] || [];
      if (questionValues.includes(value)) {
        return {
          ...prevValues,
          [questionId]: questionValues.filter((item) => item !== value),
        };
      } else {
        return {
          ...prevValues,
          [questionId]: [...questionValues, value],
        };
      }
    });
  };

  const groupedQuestions = generalQuestionsList?.reduce((acc, question) => {
    const section = question.section || "Uncategorized";
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(question);
    return acc;
  }, {});

  function reorderLists(data) {
    Object.keys(data).forEach((key) => {
      const list = data[key];
      const reorderedList = [];
      const itemMap = new Map();

      // Create a map of items based on their `_id`
      list.forEach((item) => {
        itemMap.set(item.id, item);
      });

      // Iterate through the list and reorder dynamically
      list.forEach((item) => {
        if (!reorderedList.includes(itemMap.get(item.id))) {
          reorderedList.push(item); // Add the main item first
        }
        // Find and add all items with matching `question_id`
        const relatedItems = list.filter(
          (related) => related.question_id === item.id
        );
        relatedItems.forEach((relatedItem) => {
          if (!reorderedList.includes(relatedItem)) {
            reorderedList.push(relatedItem);
          }
        });
      });

      // Add any remaining items that weren't matched
      const remainingItems = list.filter(
        (item) => !reorderedList.includes(item)
      );
      reorderedList.push(...remainingItems);

      // Update the original list with the reordered one
      data[key] = reorderedList;
    });

    return data;
  }

  let questionNumber = 1;

  return (
    <DashboardLayout>
      <Container className={classes.container}>
        <div className={classes.buttonContainer}>
          <CeroButton
            buttonText="Send Questionnaire to Suppliers"
            variant="contained"
            className={classes.buttonPrimary}
            onClick={() => {
              if (readOnlyMode) return;
              setOpen(true);
            }}
            disabled={readOnlyMode}
          />
        </div>
        <Box className={classes.box}>
          <Typography fontWeight={600}>
            SUSTAINABILITY RISK ASSESSMENT QUESTIONNAIRE
          </Typography>
          {Object.entries(reorderLists(groupedQuestions))?.map(
            ([sectionTitle, questions], index) => {
              const romanNumbers = [
                "I",
                "II",
                "III",
                "IV",
                "V",
                "VI",
                "VII",
                "VIII",
                "IX",
                "X",
              ];

              return (
                <Box key={sectionTitle} mb={4}>
                  {/* 🏷️ Section Title with Roman Number */}
                  <Typography
                    fontSize={20}
                    fontWeight={600}
                    className={classes.sectionTitle}
                    mb={2}
                    sx={{ textDecoration: "none" }}
                  >
                    {`${romanNumbers[index] || index + 1}. ${sectionTitle}`}
                  </Typography>
                  {questions?.map((question) => {
                    if (question.question_id === null) {
                      const currentQuestionNumber = questionNumber;
                      questionNumber += 1;
                      return (
                        <Grid
                          key={question.id}
                          mb={3}
                          className={
                            question.question_type === "text"
                              ? classes.inputGrid
                              : classes.gridItem
                          }
                        >
                          <Typography
                            fontSize={18}
                            fontWeight={500}
                            variant="body1"
                            className={classes.mainQuestion}
                          >
                            {`${currentQuestionNumber}. ${question.question}`}
                          </Typography>
                          <Box
                            className={classes.layout}
                            display="flex"
                            flexDirection="column"
                            paddingLeft={6}
                          >
                            {(question.question_type === "choose_from_option" ||
                              question.question_type === "choose_from_topic" ||
                              question.question_type ===
                                "multiselect_check_box") &&
                              question.options.map((option, idx) => (
                                <FormControlLabel
                                  key={idx}
                                  control={
                                    <Checkbox
                                      className={classes.selectCheckbox}
                                      checked={
                                        selectedValues[question.id]?.includes(
                                          option
                                        ) || false
                                      }
                                      onChange={(e) =>
                                        handleCheckboxChange(e, question.id)
                                      }
                                      value={option}
                                      sx={{
                                        color: "#5A404E !important",
                                        "&.Mui-checked": {
                                          color: "#8C2148 !important",
                                        },
                                      }}
                                      disabled
                                    />
                                  }
                                  label={option}
                                  className={classes.label}
                                />
                              ))}
                            {question.question_type === "text" && (
                              <Box>
                                <CeroInput
                                  classes={{
                                    container: classes.inputContainer,
                                  }}
                                  rows={3}
                                  placeholder={"Please type your Answer"}
                                  id={`answer-${question.id}`}
                                  name={`answer-${question.id}`}
                                  label="Please type your Answer"
                                  multiline
                                  disabled
                                />
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      );
                    }
                    const subQuestionLetter = String.fromCharCode(
                      96 +
                        generalQuestionsList
                          .filter((q) => q.question_id === question.question_id)
                          .indexOf(question) +
                        1
                    );
                    return (
                      <Grid key={question.id} paddingLeft={6} mb={2}>
                        <Typography className={classes.subQuestion}>
                          {`${subQuestionLetter}. ${question.question}`}
                        </Typography>
                        {question.options.map((option, idx) => (
                          <FormControlLabel
                            key={idx}
                            control={
                              <Checkbox
                                className={classes.selectCheckbox}
                                checked={
                                  selectedValues[question.id]?.includes(
                                    option
                                  ) || false
                                }
                                onChange={(e) =>
                                  handleCheckboxChange(e, question.id)
                                }
                                value={option}
                                sx={{
                                  color: "#5A404E !important",
                                  "&.Mui-checked": {
                                    color: "#8C2148 !important",
                                  },
                                }}
                                disabled
                              />
                            }
                            label={option}
                            className={classes.label}
                          />
                        ))}
                        {question.question_type === "text" && (
                          <Box>
                            <CeroInput
                              classes={{ container: classes.inputContainer }}
                              rows={2}
                              placeholder={"Please type your Answer"}
                              id={`answer-${question.id}`}
                              name={`answer-${question.id}`}
                              label="Please type your Answer"
                              multiline
                              disabled
                            />
                          </Box>
                        )}
                        {question.question_type === "upload" && (
                          <Box className={classes.upload}>
                            <Button
                              component="label"
                              role={undefined}
                              variant="contained"
                              tabIndex={-1}
                              disabled
                              className={classes.buttonPrimary}
                              startIcon={<CloudUploadIcon />}
                            >
                              Upload files
                            </Button>
                          </Box>
                        )}
                      </Grid>
                    );
                  })}
                </Box>
              );
            }
          )}
        </Box>
        <CeroPopUp
          primaryPopUp={{
            open: open,
            onClose: () => setOpen(false),
            content: SendQuestions({ setOpen, sendQuestionnairesStatus }),
            header: { title: "" },
          }}
          classes={{ dialog: classes.popUpdialog }}
        />
      </Container>
    </DashboardLayout>
  );
};

export default SustainabilityRiskAssessmentSurvey;
